import { ACCOUNT_IDENTIFIER_PAIRS } from './fieldNames';
import type { AccountIdField } from './fieldNames';

type IdentifierValue = number | string | null | undefined;

/**
 * Picks the id field that matches USE_ACCOUNT_UUID: the integer column when `useUuid` is false,
 * the paired uuid column when true. Pass the integer key (e.g. `accountId`); the uuid name is
 * looked up in {@link ACCOUNT_IDENTIFIER_PAIRS}.
 *
 * @param obj Row or payload that may contain either or both sides of the pair.
 * @param field Integer-side key ({@link AccountIdField} / {@link ACCOUNT_IDENTIFIER_PAIRS}).
 * @param useUuid Flag state for the account-identity contract.
 * @returns The value at the selected property (possibly undefined).
 */
function pickAccountIdentifier(
  obj: object,
  field: AccountIdField,
  useUuid: boolean
): IdentifierValue {
  const record = obj as Record<string, unknown>;
  if (useUuid) {
    const uuidField = ACCOUNT_IDENTIFIER_PAIRS[field];
    return record[uuidField] as IdentifierValue;
  }
  return record[field] as IdentifierValue;
}

/**
 * Array variant of {@link pickAccountIdentifier}. For types with parallel arrays
 * (e.g. `updatedBy: number[]` / `updatedByUuid: string[]`).
 *
 * Returns the array at the selected property, or `[]` if missing.
 */
function pickAccountIdentifierArray(
  obj: object,
  field: AccountIdField,
  useUuid: boolean
): Array<string | number> {
  const record = obj as Record<string, unknown>;
  const key = useUuid ? ACCOUNT_IDENTIFIER_PAIRS[field] : field;
  return (record[key] as Array<string | number>) ?? [];
}

export { pickAccountIdentifier, pickAccountIdentifierArray };
export type { IdentifierValue };
